import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onDark = !scrolled && !open && location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const links = [
    { label: t.nav.home, href: "/#startseite", id: "nav-link-home" },
    { label: t.nav.services, href: "/#leistungen", id: "nav-link-services" },
    { label: t.nav.references, href: "/#referenzen", id: "nav-link-references" },
    { label: t.nav.about, href: "/#ueber-uns", id: "nav-link-about" },
    { label: t.nav.contact, href: "/#kontakt", id: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open
          ? "border-border bg-white/85 backdrop-blur-md"
          : "border-transparent bg-white/0"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-[72px] md:px-8">
        <Link to="/#startseite" data-testid="nav-logo" aria-label="ArStudio Elektro">
          <span className="flex items-center gap-2.5">
            <span className={`grid h-8 w-8 place-items-center font-display text-sm font-bold tracking-tight transition-colors ${onDark ? "bg-white text-navy" : "bg-navy text-white"}`}>
              A
            </span>
            <span className={`font-display text-lg font-bold tracking-tight transition-colors ${onDark ? "text-white" : "text-navy"}`}>
              ArStudio Elektro
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className={`group relative text-sm font-medium transition-colors ${
                onDark ? "text-white/80 hover:text-white" : "text-navy/80 hover:text-navy"
              }`}
            >
              {l.label}
              <span className={`absolute -bottom-1 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${onDark ? "bg-white" : "bg-brand"}`} />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div
            className={`flex items-center border font-mono text-xs transition-colors ${
              onDark ? "border-white/30 bg-white/10" : "border-border bg-white"
            }`}
            data-testid="language-switch"
          >
            {["de", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1.5 uppercase tracking-wider transition-colors ${
                  lang === l
                    ? onDark
                      ? "bg-white text-navy"
                      : "bg-navy text-white"
                    : onDark
                      ? "text-white/70 hover:text-white"
                      : "text-navy/60 hover:text-navy"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="/#kontakt"
            data-testid="nav-cta-button"
            className="group flex items-center gap-1.5 bg-navy px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          className={`grid h-10 w-10 place-items-center lg:hidden ${open ? "text-navy" : onDark ? "text-white" : "text-navy"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-border bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-${l.id}`}
                  className="border-b border-border py-3.5 text-base font-medium text-navy"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center border border-border font-mono text-xs">
                  {["de", "en"].map((l) => (
                    <button
                      key={l}
                      data-testid={`mobile-lang-switch-${l}`}
                      onClick={() => setLang(l)}
                      className={`px-3 py-2 uppercase tracking-wider ${
                        lang === l ? "bg-navy text-white" : "text-navy/60"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <a
                  href="/#kontakt"
                  data-testid="mobile-nav-cta-button"
                  className="bg-navy px-4 py-2.5 text-sm font-medium text-white"
                >
                  {t.nav.cta}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
